import { api } from '@/lib/api'

import type {
	Playbook,
	PlaybookActivityDetailsRequest,
	PlaybookData,
	PlaybookPromptDecision,
	PlaybookPromptDecisionData,
	PlaybooksRequest,
	Prompt,
	RunPlaybookData,
	WorkflowActionDetails,
	WorkflowDefinition,
} from './types/workflow'

/**
 * Fetch playbook list
 * @param tenant - The tenant ID
 * @param [signal] - Optional AbortSignal
 */
export const fetchTenantPlaybooks = async (
	tenant: string,
	signal?: AbortSignal,
): Promise<Array<Playbook>> => {
	const {
		data: { playbooks },
	} = await api.get<PlaybooksRequest>(`/api/tenants/${tenant}/soar/playbooks`, {
		signal,
	})

	return playbooks
}

/**
 * Fetch playbook information
 * @param tenant - The tenant ID
 * @param playbookName - The playbook name
 * @param [signal] - Optional AbortSignal
 */
export const fetchWorkflowDefinition = async (
	tenant: string,
	playbookName: string,
	signal?: AbortSignal,
) => {
	const { data } = await api.get<WorkflowDefinition>(
		`/api/tenants/${tenant}/soar/playbooks/${playbookName}`,
		{
			signal,
		},
	)

	return data
}

/**
 * Fetch playbook activity details
 * @param tenant - Tenant ID
 * @param playbookName - Playbook ID
 * @param [signal] - Optional AbortSignal
 */
export const fetchPlaybookActivityDetails = async (
	tenant: string,
	playbookName: string | null,
	signal?: AbortSignal,
) => {
	if (!playbookName) throw new Error('Identifier is required')

	const { data } = await api.get<PlaybookActivityDetailsRequest>(
		`/api/tenants/${tenant}/soar/playbook_activity/${playbookName}`,
		{
			signal,
		},
	)

	return data
}

/**
 * Fetch action activity details
 * @param tenant - The tenant ID
 * @param playbook_id - The playbook ID
 * @param workflow_action_id - The action ID
 * @param [signal] - Optional AbortSignal
 */
export const fetchPlaybookActionDetails = async (
	tenant: string,
	playbook_id: string,
	workflow_action_id: string,
	signal?: AbortSignal,
): Promise<WorkflowActionDetails> => {
	if (!playbook_id || !workflow_action_id)
		throw new Error('Playbook ID and workflow action ID are required')

	const actionID = encodeURIComponent(workflow_action_id)

	const { data } = await api.get<WorkflowActionDetails>(
		`/api/tenants/${tenant}/soar/playbook_activity/${playbook_id}/actions/${actionID}`,
		{
			signal,
		},
	)

	return data
}

/**
 * Fetch playbook action prompt
 * @param tenant - Tenant ID
 * @param playbook_id - Playbook ID
 * @param workflow_action_id - Playbook Action ID
 * @param [signal] - Optional AbortSignal
 */
export const fetchPlaybookActionPrompt = async (
	tenant: string,
	playbook_id: string | null,
	workflow_action_id: string | null,
	signal?: AbortSignal,
) => {
	if (!playbook_id || !workflow_action_id)
		throw new Error('Playbook ID and workflow action ID are required')

	const { data } = await api.get<Prompt>(
		`/api/tenants/${tenant}/soar/playbook_activity/${playbook_id}/prompt/${workflow_action_id}`,
		{
			signal,
		},
	)

	return data
}

/**
 * Run a playbook
 * @param tenant - Tenant ID
 * @param playbook_data - Playbook inputs data
 */
export const runPlaybook = async (
	tenant: string,
	playbook_data: PlaybookData,
) => {
	const { data } = await api.post<RunPlaybookData>(
		`/api/tenants/${tenant}/soar/playbook_activity`,
		playbook_data,
	)

	return data
}

/**
 * Send the decision from a prompt
 * @param tenant - Tenant ID
 * @param playbook_id - Playbook ID
 * @param workflow_action_id - Playbook Action ID
 * @param playbook_data - The decision data
 */
export const postPlaybookPromptDecision = async (
	tenant: string,
	playbook_id: string,
	workflow_action_id: string,
	playbook_data: PlaybookPromptDecisionData,
) => {
	const { data } = await api.post<PlaybookPromptDecision>(
		`/api/tenants/${tenant}/soar/playbook_activity/${playbook_id}/prompt/${workflow_action_id}`,
		playbook_data,
	)

	return data
}
